import {
  useQuery,
  useMutation,
  useQueryClient,
  type MutationObserverOptions,
  type QueryClient,
} from '@tanstack/vue-query';
import { request } from '@/api/request';
import { type ApiTypedError } from '@/api/errors';
import {
  mergeQueryOptions,
  mergeMutationOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { useMode } from '@/modules/mode/composables';
import { SUPPLIER_INFO_QUERY } from '@/modules/profile/api/queryKeys';
import { REVIEW_SHARING_INBOX_QUERY } from '@/modules/review-sharing/api/queryKeys';
import { invalidateGrantEffects } from '@/modules/review-sharing/api/invalidateGrantEffects';
import type {
  DatasetRequest,
  DatasetResponse,
  OffsetPaginationResponse,
  SortDirection,
} from '@@/packages/shared/libs/search/types';
import {
  ConnectionRequestStatus,
  ConnectionRequestDirection,
  ConnectionRequestType,
  type ConnectionRequest,
  type ConnectionRequestAggregate,
} from '@@/packages/shared/types/models/connectionRequests';
import {
  MergeRequestStatus,
  type MergeRequest,
} from '@@/packages/shared/types/models/mergeRequest';
import type { Connection } from '@@/packages/shared/types/models/connection';
import type { Organisation } from '@@/packages/shared/types/models/organisation';
import type {
  ReviewSharingCategory,
  ReviewSharingGrantStatus,
} from '@@/packages/shared/types/models/reviewSharing';

import {
  NOTIFICATION_COUNT_QUERY,
  CONNECTION_REQUESTS_QUERY,
  CONNECTION_REQUESTS_SEARCH_QUERY,
  CONNECTION_REQUESTS_SEARCH_EXPORT_QUERY,
  MERGE_REQUESTS_QUERY,
  REMEDIATION_REQUESTS_SEARCH_QUERY,
  ASSESSMENT_REVIEWS_QUERY,
} from './queryKeys';

export { ConnectionRequestDirection, ConnectionRequestType };

export type NotificationCountResponse = {
  connectionRequests: number;
  remediationRequests: number;
  mergeRequests: number;
  reviewSharingRequests: number;
  total: number;
};

type GetMergeRequestsResponse = {
  mergeRequest: MergeRequest;
  potentialConnections: Connection[];
}[];

export function useNotificationCount(options?: QueryOptions) {
  const { currentMode } = useMode();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [NOTIFICATION_COUNT_QUERY, currentMode],
      async queryFn({ queryKey }) {
        if (!queryKey[1]) throw new Error('currentMode unexpectedly undefined');
        const res = await request<NotificationCountResponse>(
          'GET',
          `/action-centre/notifications?mode=${queryKey[1]}`,
        );
        return res.data;
      },
      enabled: () => !!currentMode.value,
    }),
  );

  return {
    ...query,
    notifications: query.data,
    total: computed(() => query.data.value?.total),
    connectionRequests: computed(() => query.data.value?.connectionRequests),
  };
}

export function useAcceptMergeRequest(options?: MutationOptions<MergeRequest, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(id) {
        const res = await request<MergeRequest>('POST', `/merge-requests/${id}/accept`, {});
        return res.data;
      },
      async onSuccess(_data, id) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CONNECTION_REQUESTS_QUERY],
          }),
          queryClient.invalidateQueries({
            queryKey: [NOTIFICATION_COUNT_QUERY],
          }),
        ]);

        queryClient.setQueriesData<GetMergeRequestsResponse>(
          { queryKey: [MERGE_REQUESTS_QUERY] },
          (oldData) => {
            if (!oldData) return undefined;
            return oldData.filter(({ mergeRequest }) => mergeRequest.id !== id);
          },
        );
      },
    }),
  );
}

export function useRejectMergeRequest(options?: MutationOptions<MergeRequest, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(id) {
        const res = await request<MergeRequest>('POST', `/merge-requests/${id}/reject`);
        return res.data;
      },
      async onSuccess(_data, id) {
        await queryClient.invalidateQueries({
          queryKey: [NOTIFICATION_COUNT_QUERY],
        });

        queryClient.setQueriesData<GetMergeRequestsResponse>(
          { queryKey: [MERGE_REQUESTS_QUERY] },
          (oldData) => {
            if (!oldData) return undefined;
            return oldData.filter(({ mergeRequest }) => mergeRequest.id !== id);
          },
        );
      },
    }),
  );
}

// Legacy function for Vuex store (TODO: replace with composables)
export const getRemediationRequests = (direction: string, status: string) =>
  request('GET', `/remediation/requests?direction=${direction}&status=${status}`);

export enum ConnectionRequestFilterStatus {
  All = 'all',
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Cancelled = 'cancelled',
  Expired = 'expired',
}

export enum SearchIdentifier {
  Unified = 'unified',
}

export enum SearchOperator {
  Contains = 'contains',
}

type SearchFilter = {
  identifier: SearchIdentifier;
  operator: SearchOperator;
  value: string;
};

export enum RemediationRequestDirection {
  Sent = 'sent',
  Received = 'received',
  All = 'all',
}

export enum RemediationRequestFilterStatus {
  All = 'all',
  AwaitingResponse = 'awaiting_response',
  AwaitingReview = 'awaiting_review',
  Completed = 'completed',
}

export enum RemediationRequestStatus {
  AwaitingResponse = 'awaiting_response',
  AwaitingReview = 'awaiting_review',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

type RemediationRequestCounterparty = {
  name: string;
  logo: string;
};

export type RemediationRequest = {
  id: string;
  supplierID: string;
  clientID: string;
  connectionID: string;
  controlID: string;
  productID: string | null;
  reason: string;
  deadline: string;
  createdAt: string;
  createdBy: string | null;
  cancelled: boolean;
  cancelledAt: string | null;
  cancelledBy: string | null;
  completed: boolean;
  completedAt: string | null;
  completedBy: string | null;
  responded: boolean;
  respondedAt: string | null;
  respondedBy: string | null;
};

export type RemediationRequestAggregate = RemediationRequest & {
  status: RemediationRequestStatus;
  org: RemediationRequestCounterparty;
};

type RemediationRequestsSearchParams = {
  direction: RemediationRequestDirection;
  status: RemediationRequestFilterStatus;
  search?: SearchFilter;
};

export type RemediationRequestsSearchRequest = DatasetRequest & RemediationRequestsSearchParams;

export type RemediationRequestsSearchResponse = {
  results: RemediationRequestAggregate[];
  datasetResponse: DatasetResponse;
};

export type ConnectionRequestFilters = {
  direction: ConnectionRequestDirection;
  status: ConnectionRequestFilterStatus;
  search?: SearchFilter;
};

type ConnectionRequestSearchParams = ConnectionRequestFilters;

export type ConnectionRequestsSearchRequest = Omit<DatasetRequest, 'filter'> &
  ConnectionRequestSearchParams;

export type ConnectionRequestsSearchResponse = {
  items: ConnectionRequestAggregate[];
  page: OffsetPaginationResponse;
};

type ConnectionRequestsExportRequest = ConnectionRequestSearchParams;

export type ConnectionRequestsExportRow = {
  supplierID?: string;
  supplierName: string;
  clientName: string;
  sender: string;
  sentDate: string;
  status: ConnectionRequestStatus;
  type: ConnectionRequestType;
  message: string;
  pointOfContactName: string;
  pointOfContactEmail: string;
  statusUpdatedAt: string;
};

type ConnectionRequestsExportResponse = {
  filename: string;
  rows: ConnectionRequestsExportRow[];
};

/**
 * Scopes a connection-request search to the side of the book the user is
 * looking at. The endpoint defaults to `all`, which for an org that is both a
 * client and a supplier mixes the requests it sent as a client together with
 * the ones it sent as a supplier, so every search sends the current mode.
 */
function withOrgMode<T extends object>(payload: MaybeRefOrGetter<T>) {
  const { currentClientOrSupplierMode } = useMode();

  return computed(() => ({ ...toValue(payload), orgMode: currentClientOrSupplierMode.value }));
}

export function useConnectionRequestsSearch(
  payload: MaybeRefOrGetter<ConnectionRequestsSearchRequest>,
  options?: QueryOptions,
) {
  const scopedPayload = withOrgMode(payload);

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CONNECTION_REQUESTS_SEARCH_QUERY, scopedPayload] as const,
      async queryFn({ queryKey }) {
        const res = await request<ConnectionRequestsSearchResponse>(
          'POST',
          '/connection-requests/search',
          toValue(queryKey[1]),
        );
        return res.data;
      },
    }),
  );

  const connectionRequests = computed(() => query.data.value?.items ?? []);
  const total = computed(() => query.data.value?.page?.total ?? 0);

  return {
    ...query,
    connectionRequests,
    total,
  };
}

export function useConnectionRequestsExport(
  payload: MaybeRefOrGetter<ConnectionRequestsExportRequest>,
  options?: QueryOptions,
) {
  const scopedPayload = withOrgMode(payload);

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CONNECTION_REQUESTS_SEARCH_EXPORT_QUERY, scopedPayload] as const,
      async queryFn({ queryKey }) {
        const { search, ...rest } = toValue(queryKey[1]);
        const res = await request<ConnectionRequestsExportResponse>(
          'GET',
          '/connection-requests/search/export',
          null,
          search ? { ...rest, search: JSON.stringify(search) } : rest,
        );
        return res.data;
      },
    }),
  );

  const filename = computed(() => query.data.value?.filename ?? '');
  const connectionRequestRows = computed(() => query.data.value?.rows ?? []);

  return {
    ...query,
    filename,
    connectionRequestRows,
  };
}

export enum ConnectionRequestAction {
  Accept = 'accept',
  Cancel = 'cancel',
  Reject = 'reject',
  Resend = 'resend',
}

type ConnectionRequestActionRequest =
  | { action: ConnectionRequestAction.Accept }
  | { action: ConnectionRequestAction.Cancel }
  | { action: ConnectionRequestAction.Reject; rejectionMessage: string }
  | {
      action: ConnectionRequestAction.Resend;
      contactName: string;
      contactEmail: string;
      message?: string;
    };

type ConnectionRequestActionResponse = {
  connectionRequest: ConnectionRequest;
  connectionID?: string;
};

enum ConnectionRequestActionErrorReasonCode {
  ResendEmailUnchanged = 'connection_request_resend_email_unchanged',
}

export function canResendConnectionRequest(
  direction: ConnectionRequestDirection,
  status: ConnectionRequestStatus,
) {
  return (
    direction === ConnectionRequestDirection.Sent && status === ConnectionRequestStatus.Pending
  );
}

export function canCancelConnectionRequest(
  direction: ConnectionRequestDirection,
  status: ConnectionRequestStatus,
) {
  return (
    direction === ConnectionRequestDirection.Sent && status === ConnectionRequestStatus.Pending
  );
}

export function canReviewConnectionRequest(
  direction: ConnectionRequestDirection,
  status: ConnectionRequestStatus,
) {
  return (
    direction === ConnectionRequestDirection.Received && status === ConnectionRequestStatus.Pending
  );
}

export function useConnectionRequestAction(
  options?: MutationObserverOptions<
    ConnectionRequestActionResponse,
    ApiTypedError<ConnectionRequestActionErrorReasonCode>,
    { id: string; payload: ConnectionRequestActionRequest },
    unknown
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ id, payload }) {
        const res = await request<ConnectionRequestActionResponse>(
          'PUT',
          `/connection-requests/${id}/action`,
          payload,
        );
        return res.data;
      },
      async onSuccess() {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CONNECTION_REQUESTS_SEARCH_QUERY],
          }),
          queryClient.invalidateQueries({
            queryKey: [CONNECTION_REQUESTS_QUERY],
          }),
          queryClient.invalidateQueries({
            queryKey: [NOTIFICATION_COUNT_QUERY],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIER_INFO_QUERY] }),
        ]);
      },
    }),
  );
}

type MergeRequestsSearchRequest = {
  statuses: MergeRequestStatus[];
  datasetRequest: DatasetRequest;
};

export type MergeRequestSearchResult = {
  mergeRequest: MergeRequest;
  sourceOrg: Organisation;
  pendingConnectionRequestCount: number;
};

export type MergeRequestsSearchResponse = {
  mergeRequests: MergeRequestSearchResult[];
  datasetResponse: DatasetResponse;
};

export function useMergeRequestsSearch(
  payload: MaybeRefOrGetter<MergeRequestsSearchRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [MERGE_REQUESTS_QUERY, toRef(payload)] as const,
      async queryFn({ queryKey }) {
        const res = await request<MergeRequestsSearchResponse>(
          'POST',
          '/merge-requests/list',
          toValue(queryKey[1]),
        );
        return res.data;
      },
    }),
  );

  const mergeRequests = computed(() => query.data.value?.mergeRequests ?? []);
  const total = computed(() => query.data.value?.datasetResponse?.page?.total ?? 0);

  return {
    ...query,
    mergeRequests,
    total,
  };
}

export function useRemediationRequestsSearch(
  payload: MaybeRefOrGetter<RemediationRequestsSearchRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [REMEDIATION_REQUESTS_SEARCH_QUERY, toRef(payload)] as const,
      async queryFn({ queryKey }) {
        const res = await request<RemediationRequestsSearchResponse>(
          'POST',
          '/remediation-requests/search',
          toValue(queryKey[1]),
        );
        return res.data;
      },
    }),
  );

  const remediationRequests = computed(() => query.data.value?.results ?? []);
  const total = computed(() => query.data.value?.datasetResponse?.page?.total ?? 0);

  return {
    ...query,
    remediationRequests,
    total,
  };
}

// One item of inbound work in the review-sharing inbox: a request relationship
// with its per-category grants collapsed onto a single row, so a request fanned
// out across seven categories is one thing to action rather than seven.
//
// The name fields are empty when the API could not resolve them (a deleted org,
// say); the ids are always present, so the row stays actionable.
export type ReviewSharingRequest = {
  supplierOrgID: string;
  supplierName: string;
  communityID: string;
  communityName: string;
  viewerOrgID: string;
  viewerName: string;
  // Always present, empty when the org has no logo and none can be derived from
  // its website.
  viewerLogo: string;
  supplierLogo: string;
  status: ReviewSharingGrantStatus;
  categories: ReviewSharingCategory[];
  requestedAt: string;
  // False when a grant would be refused: the mode no longer admits one, or owner
  // or viewer has lost the community membership or supplier connection behind it.
  // A denial carries no such preconditions, so the row is still answerable.
  grantable: boolean;
  // Categories the viewer already holds, if the caller can name them exactly -
  // the inbox can't (a pending row here doesn't carry per-category state), so
  // ReviewSharingGrantModal falls back to inferring it from `categories` for
  // that caller only.
  alreadyShared?: ReviewSharingCategory[];
};

export type ReviewSharingInboxResponse = {
  requests: ReviewSharingRequest[];
  total: number;
  limit: number;
  offset: number;
};

// The inbox is ordered by when the request was raised and nothing else, so the
// only ordering control is its direction. There is no sort field: ordering by
// viewer, supplier or community name would mean sorting on a column the
// review-sharing module does not own, which the API deliberately does not offer.
type ReviewSharingInboxParams = {
  status: ReviewSharingGrantStatus;
  sortDirection: SortDirection;
  limit: number;
  offset: number;
};

export function useReviewSharingInbox(
  payload: MaybeRefOrGetter<ReviewSharingInboxParams>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [REVIEW_SHARING_INBOX_QUERY, toRef(payload)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ReviewSharingInboxResponse>(
          'GET',
          REVIEW_SHARING_INBOX_QUERY,
          null,
          toValue(queryKey[1]),
        );
        return res.data;
      },
    }),
  );

  const requests = computed(() => query.data.value?.requests ?? []);
  const total = computed(() => query.data.value?.total ?? 0);

  return {
    ...query,
    requests,
    total,
  };
}

// No owner org: the API reads it from the session, so a caller cannot name its
// own role-org.
export type ReviewSharingRelationship = {
  supplierOrgID: string;
  communityID: string;
  viewerOrgID: string;
};

function grantsPath({ communityID, supplierOrgID, viewerOrgID }: ReviewSharingRelationship) {
  return `/review-sharing/communities/${communityID}/suppliers/${supplierOrgID}/viewers/${viewerOrgID}/grants`;
}

export type ReviewSharingGrantVariables = ReviewSharingRelationship & {
  // Unbounded by the org's own shared set: granting is the more specific act.
  categories: ReviewSharingCategory[];
  // Anything here the owner did not pick is denied, so the request is answered whole.
  pendingCategories: ReviewSharingCategory[];
};

// Both answers need it: a denial is whole, and a subset grant refuses the rest.
function denyPendingGrants(relationship: ReviewSharingRelationship) {
  return request('PATCH', grantsPath(relationship), { status: 'denied' });
}

// The badge is the one reader invalidateGrantEffects doesn't already cover
// (it lives in this module, not review-sharing's). Not awaited, as in
// useRaiseAccessRequest: mergeMutationOptions runs this before the caller's
// own onSuccess, so awaiting would hold the modal's spinner open.
function invalidateReviewSharingInbox(
  queryClient: QueryClient,
  { communityID, supplierOrgID }: ReviewSharingRelationship,
) {
  invalidateGrantEffects(queryClient, { communityID, supplierID: supplierOrgID });
  void queryClient.invalidateQueries({ queryKey: [NOTIFICATION_COUNT_QUERY] });
}

export type ReviewSharingGrantResult = {
  // False when the grant landed but refusing the rest did not, so the caller can
  // say what was shared rather than that nothing was.
  answeredWhole: boolean;
};

// Two calls because no endpoint answers a request with a subset: POST leaves the
// categories it does not name pending, which would keep the row in the inbox.
//
// Grant before deny, so a failed deny leaves the request open for another go.
// Denying first would close it even when the grant never landed.
export function useGrantReviewSharingRequest(
  options?: MutationOptions<ReviewSharingGrantResult, ReviewSharingGrantVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ categories, pendingCategories, ...relationship }) {
        await request('POST', grantsPath(relationship), { categories });

        // The picks covered everything pending, so the deny would be a no-op round trip.
        if (pendingCategories.every((category) => categories.includes(category))) {
          return { answeredWhole: true };
        }

        // Not rethrown: the grant landed, so failing here would tell the owner
        // nothing was shared when the viewer can already see it.
        try {
          await denyPendingGrants(relationship);
          return { answeredWhole: true };
        } catch (error) {
          logUnexpectedError(error);
          return { answeredWhole: false };
        }
      },
      onSuccess: (_data, variables) => invalidateReviewSharingInbox(queryClient, variables),
    }),
  );
}

// No categories: a denial is whole, and anything left pending keeps the row.
export function useDenyReviewSharingRequest(
  options?: MutationOptions<void, ReviewSharingRelationship>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(relationship) {
        await denyPendingGrants(relationship);
      },
      onSuccess: (_data, variables) => invalidateReviewSharingInbox(queryClient, variables),
    }),
  );
}

export type SupplierReviewSummary = {
  supplier: {
    id: string;
    name: string;
    logo: string;
  };
  assignedCount: number;
  reviewedCount: number;
};

export type AssessmentReviewsResponse = {
  suppliers: SupplierReviewSummary[];
  page: OffsetPaginationResponse;
};

type AssessmentReviewsParams = {
  limit: number;
  offset: number;
};

export function useAssessmentReviews(
  payload: MaybeRefOrGetter<AssessmentReviewsParams>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSESSMENT_REVIEWS_QUERY, toRef(payload)] as const,
      async queryFn({ queryKey }) {
        const params = toValue(queryKey[1]);
        const res = await request<AssessmentReviewsResponse>(
          'GET',
          `/assessment/reviews?limit=${params.limit}&offset=${params.offset}`,
        );
        return res.data;
      },
    }),
  );

  const suppliers = computed(() => query.data.value?.suppliers ?? []);
  const total = computed(() => query.data.value?.page?.total ?? 0);

  return {
    ...query,
    suppliers,
    total,
  };
}
